/**
 * Recruitment API Service Layer
 * Maps to all backend endpoints under /recruitmentService/
 * Pattern: same as leadsQueueApi.ts
 */
import api from "../../../../services/api";

const getSignature = () => localStorage.getItem("signature");

// ─────────────────────────── JOBS ────────────────────────────────────────────

export const listJobs = async (
  companyId: string,
  companyType: string,
  filters: {
    search?: string;
    department_id?: string;
    employment_type?: string;
    status?: string;
    page?: number;
    limit?: number;
    date_from?: any;
    date_to?: any;
  },
) => {
  const response = await api.post("recruitmentService/jobs/list", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    ...filters,
  });
  return response.data?.data;
};

export const getJobDetails = async (
  companyId: string,
  companyType: string,
  jobId: number,
) => {
  const response = await api.post("recruitmentService/jobs/details", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    job_id: jobId,
  });
  return response.data?.data;
};

export const createJob = async (
  companyId: string,
  companyType: string,
  jobData: Record<string, any>,
) => {
  const response = await api.post("recruitmentService/jobs/create", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    ...jobData,
  });
  return response.data?.data;
};

export const updateJob = async (
  companyId: string,
  companyType: string,
  jobId: number,
  jobData: Record<string, any>,
) => {
  const response = await api.post("recruitmentService/jobs/update", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    job_id: jobId,
    ...jobData,
  });
  return response.data?.data;
};

export const closeJob = async (
  companyId: string,
  companyType: string,
  jobId: number,
  reason?: string,
  closeOnExternalPlatforms?: boolean,
) => {
  const response = await api.post("recruitmentService/jobs/close", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    job_id: jobId,
    reason,
    close_on_external_platforms: closeOnExternalPlatforms ?? true,
  });
  return response.data?.data;
};

export const deleteJob = async (
  companyId: string,
  companyType: string,
  jobId: number,
) => {
  const response = await api.post("recruitmentService/jobs/delete", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    job_id: jobId,
  });
  return response.data?.data;
};

export const publishJob = async (
  companyId: string,
  companyType: string,
  jobId: number,
  platforms: string[],
  platformSpecificConfig?: Record<string, any>,
) => {
  const response = await api.post("recruitmentService/jobs/publish", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    job_id: jobId,
    platforms,
    platform_specific_config: platformSpecificConfig,
  });
  return response.data?.data;
};

export const getPublishingStatus = async (
  companyId: string,
  companyType: string,
  jobId: number,
) => {
  const response = await api.post("recruitmentService/jobs/publish-status", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    job_id: jobId,
  });
  return response.data?.data;
};

export const unpublishJob = async (
  companyId: string,
  companyType: string,
  jobId: number,
  platformName: string,
) => {
  const response = await api.post("recruitmentService/jobs/unpublish", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    job_id: jobId,
    platform_name: platformName,
  });
  return response.data?.data;
};

export const syncExternalJobs = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post("recruitmentService/jobs/sync", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
  });
  return response.data?.data;
};

export const getJobStatistics = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post("recruitmentService/jobs/statistics", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
  });
  return response.data?.data;
};

// ─────────────────────────── PUBLIC ──────────────────────────────────────────

export const getPublicJobs = async (filters?: {
  search?: string;
  department?: string;
  employment_type?: string;
  page?: number;
  limit?: number;
}) => {
  const response = await api.post("recruitmentService/public/jobs/list", {
    ...filters,
  });
  return response.data?.data;
};

export const getPublicJobDetails = async (jobId: string) => {
  const response = await api.post(
    `recruitmentService/public/jobs/details/${jobId}`,
    {},
  );
  return response.data?.data;
};

export const subscribeToJobAlerts = async (
  email: string,
  careerPageUuid: string,
  frequency: "daily" | "weekly" = "daily",
) => {
  const response = await api.post(
    "recruitmentService/public/jobs/alerts/subscribe",
    { email, career_page_uuid: careerPageUuid, frequency },
  );
  return response.data?.data;
};

export const getJobAlertStatus = async (
  email: string,
  careerPageUuid: string,
): Promise<{
  is_subscribed: boolean;
  frequency: "daily" | "weekly" | null;
  subscribed_at: string | null;
}> => {
  const response = await api.post(
    "recruitmentService/public/jobs/alerts/status",
    { email, career_page_uuid: careerPageUuid },
  );
  return response.data?.data;
};

export const getFeedbackSchema = async (token: string) => {
  const response = await api.get(`recruitmentService/public/feedback/${token}/schema`);
  return response.data?.data;
};

export const submitPublicFeedback = async (token: string, payload: any) => {
  const response = await api.post(`recruitmentService/public/feedback/${token}/submit`, payload);
  return response.data?.data;
};

export const getPublicSchedulingDetails = async (token: string) => {
  const response = await api.get(`recruitmentService/public/schedule/${token}`);
  return response.data?.data;
};

export const confirmPublicScheduling = async (token: string, payload: any) => {
  const response = await api.post(`recruitmentService/public/schedule/${token}/confirm`, payload);
  return response.data?.data;
};

export const createCareerPageLink = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post(
    "recruitmentService/config/career-page/link/create",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
    },
  );
  return response.data?.data;
};

export const getCareerPageLink = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post(
    "recruitmentService/config/career-page/link/get",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
    },
  );
  return response.data?.data;
};

export const getPublicCareerPageByUuid = async (
  career_page_uuid: string,
  filters?: {
    search?: string;
    department?: string;
    employment_type?: string;
    page?: number;
    limit?: number;
  },
) => {
  const response = await api.post("recruitmentService/public/career-page", {
    career_page_uuid,
    ...filters,
  });
  return response.data?.data;
};

export const toggleCareerPageLink = async (
  companyId: string,
  companyType: string,
  status: 0 | 1,
) => {
  const response = await api.post(
    "recruitmentService/config/career-page/link/toggle",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      status,
    },
  );
  return response.data?.data;
};

// ─────────────────────── GUEST AUTHENTICATION ────────────────────────────────

export const sendGuestOtp = async (
  email: string,
  companyId: string,
  companyType: string,
) => {
  const response = await api.post("recruitmentService/auth/guest/send-otp", {
    email,
    company_id: companyId,
    company_type: companyType,
  });
  return response.data?.data;
};

export const verifyGuestOtp = async (
  email: string,
  otpCode: string,
  companyId: string,
  companyType: string,
) => {
  const response = await api.post("recruitmentService/auth/guest/verify-otp", {
    email,
    otp_code: otpCode,
    company_id: companyId,
    company_type: companyType,
  });
  // On success, backend returns { token, tempProfile }
  return response.data?.data;
};

export const logoutGuest = async () => {
  const response = await api.post("recruitmentService/auth/guest/logout", {});
  return response.data?.data;
};

// ─────────────────────── HR APPLICATIONS ─────────────────────────────────────

export const listApplications = async (
  companyId: string,
  companyType: string,
  filters: {
    job_id?: number;
    status?: string;
    source?: string;
    search?: string;
    priority?: string;
    assigned_to?: number;
    min_score?: number;
    max_score?: number;
    date_from?: string;
    date_to?: string;
    page?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  },
) => {
  const response = await api.post("recruitmentService/applications/list", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    ...filters,
  });
  return response.data?.data;
};

export const getApplicationDetails = async (
  companyId: string,
  companyType: string,
  applicationId: number,
) => {
  const response = await api.post("recruitmentService/applications/details", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    application_id: applicationId,
  });
  return response.data?.data;
};

export const searchApplications = async (
  companyId: string,
  companyType: string,
  searchQuery: string,
  searchFields?: string[],
) => {
  const response = await api.post("recruitmentService/applications/search", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    search_query: searchQuery,
    search_fields: searchFields,
  });
  return response.data?.data;
};

export const updateApplicationStatus = async (
  companyId: string,
  companyType: string,
  applicationId: number,
  newStatus: string,
  notes?: string,
  options?: {
    candidate_message?: string;
    send_email_notification?: boolean;
    interview_date?: string;
    interview_time?: string;
    interview_link?: string;
    interviewer_name?: string;
    interview_mode?: string;
    interview_phone?: string;
    interview_location?: string;
    round_id?: number;
    interviewer_ids?: number[];
  },
) => {
  const response = await api.post(
    "recruitmentService/applications/update-status",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      application_id: applicationId,
      new_status: newStatus,
      notes,
      ...options,
    },
  );
  return response.data?.data;
};

export const bulkUpdateStatus = async (
  companyId: string,
  companyType: string,
  applicationIds: number[],
  newStatus: string,
  options?: {
    notes?: string;
    send_email_notification?: boolean;
    interview_date?: string;
    interview_time?: string;
    interview_link?: string;
    interviewer_name?: string;
    interview_mode?: string;
  },
) => {
  const response = await api.post(
    "recruitmentService/applications/bulk-update-status",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      application_ids: applicationIds,
      new_status: newStatus,
      ...options,
    },
  );
  return response.data?.data;
};

/** @deprecated — merged into /update-status with lean statuses */
// moveToNextStage removed


export const addApplicationTags = async (
  companyId: string,
  companyType: string,
  applicationId: number,
  tags: string[],
) => {
  const response = await api.post("recruitmentService/applications/add-tags", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    application_id: applicationId,
    tags,
  });
  return response.data?.data;
};

export const removeApplicationTag = async (
  companyId: string,
  companyType: string,
  applicationId: number,
  tag: string,
) => {
  const response = await api.post(
    "recruitmentService/applications/remove-tag",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      application_id: applicationId,
      tag,
    },
  );
  return response.data?.data;
};

export const addApplicationNote = async (
  companyId: string,
  companyType: string,
  applicationId: number,
  note: string,
) => {
  const response = await api.post("recruitmentService/applications/add-note", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    application_id: applicationId,
    note,
  });
  return response.data?.data;
};

export const getApplicationNotes = async (
  companyId: string,
  companyType: string,
  applicationId: number,
) => {
  const response = await api.post("recruitmentService/applications/get-notes", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    application_id: applicationId,
  });
  return response.data?.data;
};

export const getApplicationTimeline = async (
  companyId: string,
  companyType: string,
  applicationId: number,
) => {
  const response = await api.post("recruitmentService/applications/timeline", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    application_id: applicationId,
  });
  return response.data?.data;
};

export const getATSScoreBreakdown = async (
  companyId: string,
  companyType: string,
  applicationId: number,
) => {
  const response = await api.post(
    "recruitmentService/applications/score-breakdown",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      application_id: applicationId,
    },
  );
  return response.data?.data;
};

export const recalculateATSScore = async (
  companyId: string,
  companyType: string,
  applicationId: number,
) => {
  const response = await api.post(
    "recruitmentService/applications/recalculate-score",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      application_id: applicationId,
    },
  );
  return response.data?.data;
};

export const exportApplications = async (
  companyId: string,
  companyType: string,
  filters: Record<string, any>,
) => {
  const response = await api.post(
    "recruitmentService/applications/export",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      ...filters,
    },
    { responseType: "blob" },
  );
  return response.data;
};

export const getApplicationStatistics = async (
  companyId: string,
  companyType: string,
  jobId?: number,
) => {
  const response = await api.post(
    "recruitmentService/applications/statistics",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      job_id: jobId,
    },
  );
  return response.data?.data;
};

/** @deprecated — use updateApplicationStatus with 'rejected' status */
// shortlistCandidates & rejectCandidates removed


// ─────────────────────── INTERVIEW ROUNDS ───────────────────────────────

export const listRoundTemplates = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post("recruitmentService/rounds/templates", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
  });
  return response.data?.data;
};

export const createRoundTemplate = async (
  companyId: string,
  companyType: string,
  templateData: any,
) => {
  const response = await api.post("recruitmentService/rounds/templates/create", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    ...templateData,
  });
  return response.data?.data;
};

export const updateRoundTemplate = async (
  companyId: string,
  companyType: string,
  templateId: number,
  templateData: any,
) => {
  const response = await api.post("recruitmentService/rounds/templates/update", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    template_id: templateId,
    ...templateData,
  });
  return response.data?.data;
};

export const deleteRoundTemplate = async (
  companyId: string,
  companyType: string,
  templateId: number,
) => {
  const response = await api.post("recruitmentService/rounds/templates/delete", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    template_id: templateId,
  });
  return response.data?.data;
};

export const getJobRounds = async (
  companyId: string,
  companyType: string,
  jobId: number,
) => {
  const response = await api.post("recruitmentService/rounds/job/rounds", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    job_id: jobId,
  });
  return response.data?.data;
};

export const saveJobRounds = async (
  companyId: string,
  companyType: string,
  jobId: number,
  rounds: any[],
) => {
  const response = await api.post("recruitmentService/rounds/job/rounds/save", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    job_id: jobId,
    rounds,
  });
  return response.data?.data;
};

export const sendEmailToCandidate = async (
  companyId: string,
  companyType: string,
  applicationId: number,
  subject: string,
  message: string,
  emailType?: string,
) => {
  const response = await api.post(
    "recruitmentService/applications/send-email",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      application_id: applicationId,
      subject,
      message,
      email_type: emailType,
    },
  );
  return response.data?.data;
};

// ─────────────────────── CANDIDATE PROFILE ──────────────────────────────────

export const createCandidateProfile = async (
  companyId: string,
  companyType: string,
  profileData: Record<string, any>,
) => {
  const response = await api.post(
    "recruitmentService/candidate/profile/create",
    {
      company_id: companyId,
      company_type: companyType,
      ...profileData,
    },
  );
  return response.data?.data;
};

export const getCandidateProfile = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post(
    "recruitmentService/candidate/profile/details",
    {
      company_id: companyId,
      company_type: companyType,
    },
  );
  return response.data?.data;
};

export const getProfileCompletion = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post(
    "recruitmentService/candidate/profile/completion",
    {
      company_id: companyId,
      company_type: companyType,
    },
  );
  return response.data?.data;
};

export const uploadResume = async (formData: FormData) => {
  const response = await api.post(
    "recruitmentService/candidate/resume/upload",
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return response.data?.data;
};

export const uploadProfilePic = async (formData: FormData) => {
  const response = await api.post(
    "recruitmentService/candidate/profile-pic/upload",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data?.data;
};

// ─────────────────────── CANDIDATE APPLICATION ──────────────────────────────

export const parseResume = async (
  resumeFilePath: string,
  autoFillProfile?: boolean,
) => {
  const response = await api.post("recruitmentService/candidate/resume/parse", {
    resume_file_path: resumeFilePath,
    auto_fill_profile: autoFillProfile ?? true,
  });
  return response.data?.data;
};

export const getParseStatus = async (documentId: number) => {
  const response = await api.post(
    "recruitmentService/candidate/resume/parse-status",
    {
      document_id: documentId,
    },
  );
  return response.data?.data;
};

export const submitApplication = async (
  companyId: string,
  companyType: string,
  applicationData: Record<string, any>,
) => {
  const response = await api.post(
    "recruitmentService/candidate/application/submit",
    {
      company_id: companyId,
      company_type: companyType,
      ...applicationData,
    },
  );
  return response.data?.data;
};

export const getMyApplications = async () => {
  const response = await api.post(
    "recruitmentService/candidate/application/list",
    {},
  );
  return response.data?.data;
};

export const getCandidateApplicationDetails = async (
  companyId: string,
  companyType: string,
  applicationCode: string,
) => {
  const response = await api.post(
    "recruitmentService/candidate/application/details",
    {
      company_id: companyId,
      company_type: companyType,
      application_code: applicationCode,
    },
  );
  return response.data?.data;
};

// Candidate Scheduling APIs

export const getCandidateUpcomingInterviews = async () => {
  const response = await api.post(
    "recruitmentService/interviews/candidate/upcoming",
    {},
  );
  return response.data?.data;
};

export const candidateConfirmInterview = async (
  interviewId: number,
  confirmationStatus: "confirmed" | "declined",
  declineReason?: string,
) => {
  const response = await api.post(
    "recruitmentService/interviews/candidate/confirm",
    {
      interview_id: interviewId,
      confirmation_status: confirmationStatus,
      decline_reason: declineReason,
    },
  );
  return response.data?.data;
};

export const candidateRequestReschedule = async (
  interviewId: number,
  rescheduleReason: string,
  requestedDate?: string,
) => {
  const response = await api.post(
    "recruitmentService/interviews/candidate/request-reschedule",
    {
      interview_id: interviewId,
      reschedule_reason: rescheduleReason,
      requested_date: requestedDate,
    },
  );
  return response.data?.data;
};

export const withdrawApplication = async (
  companyId: string,
  companyType: string,
  applicationCode: string,
  withdrawalReason?: string,
  withdrawalComments?: string,
) => {
  const response = await api.post(
    "recruitmentService/candidate/application/withdraw",
    {
      company_id: companyId,
      company_type: companyType,
      application_code: applicationCode,
      withdrawal_reason: withdrawalReason,
      withdrawal_comments: withdrawalComments,
    },
  );
  return response.data?.data;
};

export const trackApplication = async (applicationCode: string) => {
  const response = await api.get(
    `recruitmentService/candidate/track/${applicationCode}`,
  );
  return response.data?.data;
};

export const checkApplicationStatus = async (
  company_id: string,
  company_type: string,
  job_id: string,
): Promise<{
  has_applied: boolean;
  application: {
    application_code: string;
    status: string;
    applied_at: string;
  } | null;
}> => {
  const response = await api.post(
    "/recruitmentService/candidate/application/check",
    {
      company_id,
      company_type,
      job_id,
    },
  );
  return response.data?.data;
};

// ─────────────────────── INTERVIEWS ─────────────────────────────────────────

export const listInterviews = async (
  companyId: string,
  companyType: string,
  filters: {
    application_id?: number;
    status?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    limit?: number;
  },
) => {
  const response = await api.post("recruitmentService/interviews/list", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    ...filters,
  });
  return response.data?.data;
};

/** @deprecated — use scheduleInterviewRound */
// scheduleInterview removed

export const scheduleInterviewRound = async (
  companyId: string,
  companyType: string,
  data: {
    application_id: number;
    round_type: string;
    round_number: number;
    scheduled_at: string;
    interviewer_ids?: number[];
    duration_minutes?: number;
    interview_mode?: "video_call" | "phone" | "in_person";
    interview_link?: string;
    interview_location?: string;
    send_notification?: boolean;
  },
) => {
  const response = await api.post("recruitmentService/interviews/schedule-round", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    ...data,
  });
  return response.data?.data;
};


export const rescheduleInterview = async (
  companyId: string,
  companyType: string,
  interviewId: number,
  newScheduledAt: string,
  rescheduleReason?: string,
  durationMinutes?: number,
  sendNotification?: boolean,
) => {
  const response = await api.post("recruitmentService/interviews/reschedule", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    interview_id: interviewId,
    new_scheduled_at: newScheduledAt,
    reschedule_reason: rescheduleReason,
    duration_minutes: durationMinutes,
    send_notification: sendNotification,
  });
  return response.data?.data;
};

export const editInterview = async (
  companyId: string,
  companyType: string,
  interviewId: number,
  data: {
    scheduled_at?: string;
    duration_minutes?: number;
    interview_mode?: string;
    meeting_link?: string;
    meeting_location?: string;
    meeting_platform?: string;
    interviewer_ids?: number[];
    instructions?: string;
    send_notification?: boolean;
  },
) => {
  const response = await api.post("recruitmentService/interviews/edit", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    interview_id: interviewId,
    ...data,
  });
  return response.data?.data;
};

export const cancelInterview = async (
  companyId: string,
  companyType: string,
  interviewId: number,
  cancellationReason:
    | "candidate_unavailable"
    | "interviewer_unavailable"
    | "position_filled"
    | "candidate_withdrawn"
    | "other",
  cancellationNotes?: string,
  sendNotification?: boolean,
) => {
  const response = await api.post("recruitmentService/interviews/cancel", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    interview_id: interviewId,
    cancellation_reason: cancellationReason,
    cancellation_notes: cancellationNotes,
    send_notification: sendNotification,
  });
  return response.data?.data;
};

export const submitInterviewFeedback = async (
  companyId: string,
  companyType: string,
  feedback: {
    interview_id: number;
    overall_rating: number;
    technical_skills_rating?: number;
    communication_rating?: number;
    problem_solving_rating?: number;
    cultural_fit_rating?: number;
    strengths?: string;
    weaknesses?: string;
    detailed_feedback: string;
    recommendation: string;
    questions_asked?: string[];
    notes?: string;
  },
) => {
  const response = await api.post(
    "recruitmentService/interviews/submit-feedback",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      ...feedback,
    },
  );
  return response.data?.data;
};

export const getInterviewFeedback = async (
  companyId: string,
  companyType: string,
  interviewId: number,
) => {
  const response = await api.post(
    "recruitmentService/interviews/get-feedback",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      interview_id: interviewId,
    },
  );
  return response.data?.data;
};

export const getMyPendingFeedbacks = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post(
    "recruitmentService/interviews/my-pending-feedbacks",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
    },
  );
  return response.data?.data;
};

export const getInterviewStatistics = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post("recruitmentService/interviews/statistics", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
  });
  return response.data?.data;
};

export const completeInterview = async (
  companyId: string,
  companyType: string,
  interviewId: number,
  notes?: string,
) => {
  const response = await api.post("recruitmentService/interviews/complete", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    interview_id: interviewId,
    notes,
  });
  return response.data?.data;
};

export const updateInterviewStatus = async (
  companyId: string,
  companyType: string,
  interviewId: number,
  newStatus: string,
  notes?: string,
) => {
  const response = await api.post(
    "recruitmentService/interviews/update-status",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      interview_id: interviewId,
      new_status: newStatus,
      notes,
    },
  );
  return response.data?.data;
};

export const getAvailableInterviewers = async (
  companyId: string,
  companyType: string,
  date?: string,
) => {
  const response = await api.post(
    "recruitmentService/interviews/available-interviewers",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      date: date || new Date().toISOString().split("T")[0],
    },
  );
  return response.data?.data;
};

// ─────────────────────── DOCUMENTS ──────────────────────────────────────────

export const requestDocument = async (
  companyId: string,
  companyType: string,
  applicationId: number,
  documentType: string,
  documentName: string,
  options?: {
    description?: string;
    is_mandatory?: boolean;
    deadline?: string | null;
    send_notification?: boolean;
  },
) => {
  const response = await api.post("recruitmentService/documents/hr/request", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    application_id: applicationId,
    document_type: documentType,
    document_name: documentName,
    description: options?.description ?? null,
    is_mandatory: options?.is_mandatory ?? true,
    deadline: options?.deadline ?? null,
    send_notification: options?.send_notification ?? true,
  });
  return response.data?.data;
};
export const getApplicationDocuments = async (
  companyId: string,
  companyType: string,
  applicationId: number,
) => {
  const response = await api.post(
    "recruitmentService/documents/hr/application",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      application_id: applicationId,
    },
  );
  return response.data?.data;
};

export const verifyDocument = async (
  companyId: string,
  companyType: string,
  documentId: number,
  status: "verified" | "rejected" | any,
  verificationNotes?: string,
) => {
  const response = await api.post("recruitmentService/documents/hr/verify", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    document_id: documentId,
    status,
    verification_notes: verificationNotes,
  });
  return response.data?.data;
};

export const candidateUploadDocument = async (formData: FormData) => {
  const response = await api.post(
    "recruitmentService/documents/candidate/upload",
    formData,
  );
  return response.data?.data;
};

export const getCandidatePendingRequests = async (candidateId: number) => {
  const response = await api.post(
    "recruitmentService/documents/candidate/pending-requests",
    {},
  );
  return response.data?.data;
};

// ─────────────────────── REFERRALS ──────────────────────────────────────────

export const submitReferral = async (
  companyId: string,
  companyType: string,
  data: {
    candidate_name: string;
    candidate_email: string;
    candidate_phone: string;
    job_id: number;
    relationship: string;
    why_good_fit: string;
    linkedin_profile?: string;
    total_experience_years?: number;
    current_company?: string;
    current_designation?: string;
    resume_link?: string;
    skills?: string[];
    additional_notes?: string;
  },
) => {
  const response = await api.post(
    "recruitmentService/referrals/employee/submit",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      ...data,
    },
  );
  return response.data?.data;
};

export const getMyReferrals = async (
  companyId: string,
  companyType: string,
  filters?: {
    status?: string;
    page?: number;
    limit?: number;
  },
) => {
  const response = await api.post(
    "recruitmentService/referrals/employee/my-referrals",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      ...filters,
    },
  );
  return response.data?.data;
};

export const listHRReferrals = async (
  companyId: string,
  companyType: string,
  filters?: {
    job_id?: number;
    referred_by?: number;
    status?: string;
    date_from?: string;
    date_to?: string;
    page?: number;
    limit?: number;
  },
) => {
  const response = await api.post("recruitmentService/referrals/hr/list", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    ...filters,
  });
  return response.data?.data;
};

// ─────────────────────── REFERRAL REWARDS ────────────────────────────────────

export const listReferralRewards = async (
  companyId: string,
  companyType: string,
  filters: {
    status?: string;
    stage?: string;
    search?: string;
    page?: number;
    limit?: number;
  },
) => {
  const response = await api.post("recruitmentService/referrals-rewards/list", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    ...filters,
  });
  return response.data?.data;
};

export const updateReferralRewardStatus = async (
  companyId: string,
  companyType: string,
  rewardId: number,
  status: string,
  notes?: string,
) => {
  const response = await api.post(
    "recruitmentService/referrals-rewards/update-status",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      reward_id: rewardId,
      status,
      notes,
    },
  );
  return response.data?.data;
};

export const getReferralRewardSummary = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post("recruitmentService/referrals-rewards/summary", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
  });
  return response.data?.data;
};

export const getReferralStatistics = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post(
    "recruitmentService/referrals/hr/statistics",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
    },
  );
  return response.data?.data;
};

export const getTopReferrers = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post(
    "recruitmentService/referrals/hr/top-referrers",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
    },
  );
  return response.data?.data;
};

export const updateReferralStatus = async (
  companyId: string,
  companyType: string,
  referralId: number,
  status: string,
  statusNotes?: string,
  sendNotification?: boolean,
) => {
  const response = await api.post(
    "recruitmentService/referrals/hr/update-status",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      referral_id: referralId,
      status,
      status_notes: statusNotes,
      send_notification: sendNotification,
    },
  );
  return response.data?.data;
};

export const convertReferralToApplication = async (
  companyId: string,
  companyType: string,
  referralId: number,
) => {
  const response = await api.post(
    "recruitmentService/referrals/hr/convert-to-application",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      referral_id: referralId,
    },
  );
  return response.data?.data;
};

// ─────────────────────── SETTINGS ───────────────────────────────────────────

export const getCareerPageSettings = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post("recruitmentService/config/career-page/get", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
  });
  return response.data?.data;
};

export const updateCareerPageSettings = async (
  companyId: string,
  companyType: string,
  settings: Record<string, any>,
) => {
  const response = await api.post(
    "recruitmentService/config/career-page/update",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      company_name: settings?.company_name,
      primary_color: settings?.primary_color,
      about_company: settings?.description,
      benefits_overview: settings?.benefits_overview,
      contact_email: settings?.contact_email,
      contact_phone: settings?.contact_phone,
      social_links: settings?.social_links,
      seo_meta_title: settings?.meta_title,
      seo_meta_description: settings?.meta_description,
      seo_keywords: settings?.seo_keywords,
    },
  );
  return response.data?.data;
};

export const uploadCareerPageAsset = async (formData: FormData) => {
  formData.append("signature", getSignature() || "");
  const response = await api.post(
    "recruitmentService/config/career-page/asset/upload",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data?.data;
};

export const deleteCareerPageAsset = async (
  company_id: string | number,
  company_type: string,
  asset_type: "logo" | "banner",
) => {
  const response = await api.post(
    "/recruitmentService/config/career-page/asset/delete",
    {
      signature: getSignature(),
      company_id,
      company_type,
      asset_type,
    },
  );
  return response.data?.data;
};

export const getIntegrations = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post(
    "recruitmentService/config/integrations/list",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
    },
  );
  return response.data?.data;
};

export const updateIntegrationCredentials = async (
  companyId: string,
  companyType: string,
  integrationKey: string,
  apiKey: string,
  apiSecret: string,
  accessToken: string,
) => {
  const response = await api.post(
    "recruitmentService/config/integrations/credentials/update",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      platform_name: integrationKey,
      api_key: apiKey,
      api_secret: apiSecret,
      access_token: accessToken,
    },
  );
  return response.data?.data;
};

export const updateSyncFrequency = async (
  companyId: string,
  companyType: string,
  integrationKey: string,
  syncFrequencyMinutes: number,
) => {
  const response = await api.post(
    "recruitmentService/config/integrations/sync-frequency/update",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      platform_name: integrationKey,
      sync_frequency_minutes: syncFrequencyMinutes,
    },
  );
  return response.data?.data;
};

export const testIntegrationConnection = async (
  companyId: string,
  companyType: string,
  integrationKey: string,
) => {
  const response = await api.post(
    "recruitmentService/config/integrations/connection/test",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      platform_name: integrationKey,
    },
  );
  return response.data?.data;
};

export const toggleIntegration = async (
  companyId: string,
  companyType: string,
  integrationKey: string,
  enabled: boolean,
) => {
  const response = await api.post(
    "recruitmentService/config/integrations/toggle",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      platform_name: integrationKey,
      is_active: enabled,
    },
  );
  return response.data?.data;
};

export const getATSScoringWeights = async (
  companyId: string,
  companyType: string,
) => {
  const response = await api.post(
    "recruitmentService/config/ats-scoring/weights/get",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
    },
  );
  return response.data?.data;
};

export const updateATSScoringWeights = async (
  companyId: string,
  companyType: string,
  weights: {
    skills_match: number;
    experience_match: number;
    education_match: number;
    keyword_match: number;
  },
) => {
  const response = await api.post(
    "recruitmentService/config/ats-scoring/weights/update",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      weights,
    },
  );
  return response.data?.data;
};

// ─────────────────────── HR CANDIDATES (Centralized) ─────────────────────────

export const listCandidates = async (
  companyId: string,
  companyType: string,
  filters: {
    search?: string;
    page?: number;
    limit?: number;
  },
) => {
  const response = await api.post(
    "recruitmentService/applications/candidates/list",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      ...filters,
    },
  );
  return response.data?.data;
};

// ─────────────────────── CANDIDATE DOCUMENT REQUESTS ─────────────────────────

/**
 * Fetch document requests with status = 'submitted' for the logged-in candidate.
 * Uses the same backend endpoint as getCandidatePendingRequests but with a
 * status filter. Falls back gracefully if the backend doesn't support it yet.
 */
export const getCandidateSubmittedRequests = async () => {
  try {
    const response = await api.post(
      "recruitmentService/documents/candidate/pending-requests",
      { status: "submitted" },
    );
    return response.data?.data;
  } catch {
    return { pending_requests: [], total_count: 0 };
  }
};

/**
 * Link a previously-uploaded file to a document request record.
 * Sets t_document_requests.status = 'submitted'.
 */
export const submitRequestedDocument = async (payload: {
  request_id: number;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  comments?: string;
}) => {
  const response = await api.post(
    "recruitmentService/documents/candidate/submit-requested",
    payload,
  );
  return response.data?.data;
};

// ─────────────────────── REFERRAL BONUSES ────────────────────────────────────

export const approveReferralBonus = async (
  companyId: string,
  companyType: string,
  referralId: number,
  bonusAmount: number,
  approvalNotes?: string,
) => {
  const response = await api.post("recruitmentService/referrals/approve-bonus", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    referral_id: referralId,
    bonus_amount: bonusAmount,
    approval_notes: approvalNotes,
  });
  return response.data?.data;
};

export const processReferralPayment = async (
  companyId: string,
  companyType: string,
  referralId: number,
  paymentReference?: string,
  paymentDate?: string,
  paymentNotes?: string,
) => {
  const response = await api.post("recruitmentService/referrals/process-payment", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    referral_id: referralId,
    payment_reference: paymentReference,
    payment_date: paymentDate,
    payment_notes: paymentNotes,
  });
  return response.data?.data;
};
export const getInterviewDetails = async (
  companyId: string,
  companyType: string,
  interviewId: number,
) => {
  const response = await api.post("recruitmentService/interviews/details", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    interview_id: interviewId,
  });
  return response.data?.data;
};

export const getAggregateInterviewFeedback = async (
  companyId: string,
  companyType: string,
  interviewId: number,
) => {
  const response = await api.post("recruitmentService/rounds/feedback/aggregate", {
    signature: getSignature(),
    company_id: companyId,
    company_type: companyType,
    interview_id: interviewId,
  });
  return response.data?.data;
};

// ─────────────────────── APPLICATION ROUNDS (PIPELINE) ──────────────────────

export const listApplicationRounds = async (
  companyId: string,
  companyType: string,
  applicationId: number,
) => {
  const response = await api.post(
    "recruitmentService/applications/rounds/list",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      application_id: applicationId,
    },
  );
  return response.data?.data;
};

export const updateApplicationRoundResult = async (
  companyId: string,
  companyType: string,
  applicationRoundId: number,
  roundStatus: string,
  options?: {
    score?: number;
    notes?: string;
    result_override?: boolean;
    send_notification?: boolean;
  },
) => {
  const response = await api.post(
    "recruitmentService/applications/rounds/update-result",
    {
      signature: getSignature(),
      company_id: companyId,
      company_type: companyType,
      application_round_id: applicationRoundId,
      round_status: roundStatus,
      ...options,
    },
  );
  return response.data?.data;
};
